import React from 'react';
import { Box, Typography, Paper, Avatar } from '@mui/material';
import { UserRound, BookMarked, Image as ImageIcon, Search, FileText, ExternalLink } from 'lucide-react';

const WIDGET_ICONS = {
  character_card: UserRound,
  chapter_card: BookMarked,
  image_card: ImageIcon,
  search_result: Search,
  doc_link: FileText
};

const WIDGET_COLORS = {
  character_card: '#a78bfa',
  chapter_card: '#60a5fa',
  image_card: '#f472b6',
  search_result: '#34d399',
  doc_link: '#fbbf24'
};

function WidgetCard({ widget, teamId }) {
  const Icon = WIDGET_ICONS[widget.type] || Search;
  const color = WIDGET_COLORS[widget.type] || '#a78bfa';
  const isDocLink = widget.type === 'doc_link';

  const handleClick = isDocLink ? () => {
    const slug = widget.id || '';
    window.location.href = `/docs#${slug}`;
  } : undefined;

  return (
    <Paper
      variant="outlined"
      onClick={handleClick}
      sx={{
        p: 1.5,
        borderColor: 'divider',
        bgcolor: 'rgba(255,255,255,0.03)',
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        borderLeft: `3px solid ${color}`,
        maxWidth: 400,
        ...(isDocLink && {
          cursor: 'pointer',
          transition: 'all 0.15s',
          '&:hover': { borderColor: color, bgcolor: 'rgba(251,191,36,0.06)' }
        })
      }}
    >
      {widget.imageUrl ? (
        <Avatar
          src={widget.imageUrl}
          variant="rounded"
          sx={{ width: 48, height: 48, bgcolor: 'rgba(139,92,246,0.14)' }}
        >
          <Icon size={20} />
        </Avatar>
      ) : (
        <Box sx={{ width: 48, height: 48, borderRadius: 1, bgcolor: 'rgba(139,92,246,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={20} style={{ color }} />
        </Box>
      )}
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ color: 'text.primary' }}>
            {widget.name || '—'}
          </Typography>
          {widget.number && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              #{widget.number}
            </Typography>
          )}
          {isDocLink && <ExternalLink size={12} style={{ color: '#fbbf24', flexShrink: 0 }} />}
        </Box>
        {widget.summary && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, mt: 0.25 }}>
            {widget.summary}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export default function NexusWidgets({ widgets, teamId }) {
  if (!widgets || widgets.length === 0) return null;
  return (
    <Box display="flex" flexDirection="column" gap={1} mt={1}>
      {widgets.map((w, i) => (
        <WidgetCard key={i} widget={w} teamId={teamId} />
      ))}
    </Box>
  );
}
